import React from 'react';

import {Owner} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {
  ClientTagSchemaConstraint,
  RegistryLabelingStrategy,
  TagSchemaConstraint,
} from 'constants/RegistryConstants';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import {NameTemplatePart} from 'name-templates/NameTemplateConstants';
import notifications from 'notifications';
import {Organization, Plan} from 'orgs/OrganizationConstants';
import LabelingStrategyConfiguration from 'registry/labeling-strategies/LabelingStrategyConfiguration';
import {getTagSchemaSettingsUrl} from 'registry/RegistryUtil';
import {FIELD_TYPES, FieldType} from 'schemas/fields/SchemaFieldConstants';
import {getItemTypeDisplayName, itemTypeToFolderItemTypeAndSequenceType} from 'schemas/TagSchemaItemTypeUtil';
import ConstraintTable, {ConstraintTableType} from 'settings/registry/ConstraintTable';
import SetTagSchemaConstraintModal from 'settings/registry/SetTagSchemaConstraintModal';
import EntityStore from 'stores/EntityStore';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import licenses from 'user/Licenses';
import * as modals from 'util/Modals';

import InterfaceConstraintErrorsBannerStack from './InterfaceConstraintErrorsBannerStack';
import {SchemaInterface} from './SchemaInterfaceConstants';
import SchemaInterfaceMetadataSection from './SchemaInterfaceMetadataSection';
import {ClientInterfaceField} from './SchemaInterfaceUtils';
import {convertFromClientInterfaceFieldToSchemaField} from './SchemaInterfaceUtils';
import {
  FIELD_LEVEL_ERRORS,
  InterfaceConstraintErrorDisplayDataForInterface,
  SCHEMA_OR_INTERFACE_LEVEL_ERRORS,
} from './validation/SchemaInterfaceValidationUtils';

const filterFieldsByFieldTypes = (
  fieldType: FieldType,
  fields: Array<ClientInterfaceField>
): Array<ClientInterfaceField> =>
  fields.filter((field: ClientInterfaceField) => field.fieldType === fieldType);

export interface SchemaInterfaceMetadataSettingsProps {
  interfaceId?: string;
  interface: SchemaInterface | null;
  ownerId: string;
  ownerChoices: Array<Owner> | null;
  setOwnerId: (ownerId: string) => void;
  name: string;
  setName: (name: string) => void;
  sqlIdentifier: string | null;
  setSqlIdentifier: (sqlIdentifier: string) => void;
  itemType: TagSchemaItemType | null;
  setItemType: (itemType: TagSchemaItemType | null) => void;
  constraint: ClientTagSchemaConstraint | TagSchemaConstraint | null;
  setConstraint: (constraint: ClientTagSchemaConstraint | null) => void;
  handleNext?: () => void;
  fields: Array<ClientInterfaceField>;
  fieldTable: JSX.Element;
  interfaceConstraintErrors?: Array<InterfaceConstraintErrorDisplayDataForInterface> | null;
  prefix: string;
  setPrefix: (prefix: string) => void;
  labelingStrategies: Array<RegistryLabelingStrategy>;
  setLabelingStrategies: (newStrategies: Array<RegistryLabelingStrategy>) => void;
  nameTemplateParts: Array<NameTemplatePart>;
  shouldOrderPartsBySequence?: boolean;
  setNameTemplate?: (
    nameTemplateParts: Array<NameTemplatePart>,
    shouldOrderNamePartsBySequence: boolean
  ) => void;
  enforceLabelingStrategies: boolean;
  setEnforceLabelingStrategies: (enforceLabelingStrategies: boolean) => void;
}

export default function SchemaInterfaceMetadataSettings(
  props: SchemaInterfaceMetadataSettingsProps
): JSX.Element {
  let error = null;
  if (!props.name) {
    error = 'Schema needs a name';
  }

  const setItemTypeWithFieldValidation = (newItemType: TagSchemaItemType | null): void => {
    // Part and Translation links can only exist on RNA and DNA sequence typed entities
    if (
      newItemType == null ||
      (newItemType !== TagSchemaItemType.DNA_SEQUENCE && newItemType !== TagSchemaItemType.RNA_SEQUENCE)
    ) {
      const invalidPartLinkFields = filterFieldsByFieldTypes(FIELD_TYPES.PART_LINK.type, props.fields);

      const invalidTranslationLinkFields = filterFieldsByFieldTypes(
        FIELD_TYPES.TRANSLATION_LINK.type,
        props.fields
      );

      if (invalidPartLinkFields.length > 0 || invalidTranslationLinkFields.length > 0) {
        const typeDisplayName = newItemType ? getItemTypeDisplayName(newItemType) : 'None';
        const invalidLinkFieldInfo = invalidPartLinkFields
          .map((field) => `${field.displayName} (part link)`)
          .concat(invalidTranslationLinkFields.map((field) => `${field.displayName} (translation link)`));

        notifications.error(
          `Part and translation link fields are not supported for '${typeDisplayName}' entity type. \
          In order to update the entity type to '${typeDisplayName}', the following field(s) need to be updated or removed: \
          ${invalidLinkFieldInfo.join(', ')}`
        );
        return;
      }
    }
    if (newItemType == null || newItemType !== TagSchemaItemType.AA_SEQUENCE) {
      const invalidAAPartLinkFields = filterFieldsByFieldTypes(FIELD_TYPES.AA_PART_LINK.type, props.fields);

      if (invalidAAPartLinkFields.length > 0) {
        const typeDisplayName = newItemType ? getItemTypeDisplayName(newItemType) : 'None';
        const invalidLinkFieldInfo = invalidAAPartLinkFields.map(
          (field) => `${field.displayName} (part link)`
        );

        notifications.error(
          `AA Part link fields are not supported for '${typeDisplayName}' entity type. \
          In order to update the entity type to '${typeDisplayName}', the following field(s) need to be updated or removed: \
          ${invalidLinkFieldInfo.join(', ')}`
        );
        return;
      }
    }

    props.setItemType(newItemType);
  };

  const canSetItemType =
    props.constraint == null || (!props.constraint.uniqueResidues && !props.constraint.uniqueCanonicalSmiles);

  const showEnterpriseRegistryFeatures = (): boolean => {
    if (props.interfaceId) {
      const orgId = props.interface?.organization.id || '';
      const organization = EntityStore.getEntity(orgId);
      return (organization as Organization).autoEnablePlan === Plan.ELN_PRO;
    } else {
      return licenses.hasEnterprisePlan();
    }
  };

  const handleOpenConstraintModal = async (): Promise<void> => {
    const convertedFields = props.fields.map((field) => convertFromClientInterfaceFieldToSchemaField(field));
    // Interface fields cannot be computed or archived at the moment, but filtering for forwards-compatability
    const nonComputedOrArchivedFields = convertedFields.filter(
      (field) => !field.isComputed && field.archiveRecord == null
    );

    const baseBioentityType = props.itemType ? itemTypeToFolderItemTypeAndSequenceType(props.itemType) : null;
    const constraint = await legacyRunModal(SetTagSchemaConstraintModal, {
      bioentityType: baseBioentityType?.folderItemType ?? null,
      itemType: props.itemType,
      schemaFields: nonComputedOrArchivedFields,
      tableType: ConstraintTableType.INTERFACE,
    });
    if (constraint == null) {
      return;
    }
    if (props.interface?.constraint != null) {
      notifications.error('You may not add more than one constraint.');
    } else {
      props.setConstraint(constraint);
    }
  };

  const handleRemoveConstraint = (): void => {
    modals
      .confirm('Remove constraint', 'Are you sure you want to remove this constraint?', 'danger', {
        enableRedesign: true,
      })
      .done(() => {
        props.setConstraint(null);
      });
  };

  return (
    <div className="u-flex-grow u-min-width-zero">
      <div className="settingsPage-contentWrapper">
        <SchemaInterfaceMetadataSection
          isEditableWarehouseTableName={BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME}
          name={props.name}
          ownerId={props.ownerId}
          ownerChoices={props.ownerChoices}
          sqlIdentifier={props.sqlIdentifier}
          onNameChange={props.setName}
          onOwnerChange={(owner) => {
            props.setOwnerId(owner.id);
          }}
          onSqlIdentifierChange={props.setSqlIdentifier}
          interface={props.interface}
          itemType={props.itemType}
          onItemTypeChange={setItemTypeWithFieldValidation}
          canSetItemType={canSetItemType}
          prefix={props.prefix}
          onPrefixChange={props.setPrefix}
          nameTemplateParts={props.nameTemplateParts}
          shouldOrderPartsBySequence={props.shouldOrderPartsBySequence}
          onNameTemplateChange={props.setNameTemplate}
          labelingStrategies={props.labelingStrategies}
          fields={props.fields}
        />
        {BenchlingConfig.SCHEMAS_ENABLE_INTERFACE_CONSTRAINTS && (
          <ConstraintTable
            constraint={props.constraint}
            folderItemType={
              props.itemType ? itemTypeToFolderItemTypeAndSequenceType(props.itemType).folderItemType : null
            }
            onAddClick={handleOpenConstraintModal}
            onRemoveClick={handleRemoveConstraint}
            writable={true}
            tableType={ConstraintTableType.INTERFACE}
          />
        )}
        <div className="u-large-spacing-bottom">
          <InterfaceConstraintErrorsBannerStack
            interfaceConstraintErrors={props.interfaceConstraintErrors}
            onClickAction={(errorData: InterfaceConstraintErrorDisplayDataForInterface) => {
              navigateToURL(
                getTagSchemaSettingsUrl(errorData.ownerUrlId, errorData.registryId, errorData.id)
              );
            }}
            actionText="View schema"
            errorsToRender={SCHEMA_OR_INTERFACE_LEVEL_ERRORS}
          />
        </div>
        {props.fieldTable}
        <div className="u-large-spacing-bottom">
          <InterfaceConstraintErrorsBannerStack
            interfaceConstraintErrors={props.interfaceConstraintErrors}
            onClickAction={(errorData: InterfaceConstraintErrorDisplayDataForInterface) => {
              navigateToURL(
                getTagSchemaSettingsUrl(errorData.ownerUrlId, errorData.registryId, errorData.id)
              );
            }}
            actionText="View schema"
            errorsToRender={FIELD_LEVEL_ERRORS}
          />
        </div>
        {BenchlingConfig.SCHEMAS_ENABLE_INTERFACE_REGISTRY_ID_AND_NAMING_FEATURES &&
          showEnterpriseRegistryFeatures() && (
            // TODO(BNCH-74268) Disable metadata input if insufficient write privileges
            <div className="settingsPageContainer-section">
              <LabelingStrategyConfiguration
                labelingStrategies={props.labelingStrategies}
                onChange={props.setLabelingStrategies}
                nameTemplateParts={props.nameTemplateParts}
                isWritable={true}
                enforceLabelingStrategies={props.enforceLabelingStrategies}
                setEnforceLabelingStrategies={props.setEnforceLabelingStrategies}
              />
            </div>
          )}
      </div>
      {props.handleNext && (
        <TooltipWrapper tooltipTitle={error}>
          <Button
            className="u-small-spacing-right"
            buttonStyle={ButtonStyle.SECONDARY}
            disabled={error != null}
            onClick={props.handleNext}
            size={ButtonSize.MEDIUM}
            text="Next"
            dataTestAttrs={{component: 'SchemaInterfaceMetadataSettings', element: 'nextButton'}}
          />
        </TooltipWrapper>
      )}
    </div>
  );
}
